import { computed, inject, nextTick, reactive, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useDebounceFn } from '@vueuse/core'
import { isEqual, reduce } from 'lodash'
import { useRoute } from 'vue-router'

import { AnalyticsField, AnalyticsStatus } from './utils/enums'
import { WSInstance, WSResponse } from '@/services/ws/utils/types'
import { AnalyticsRequestsMap, AnalyticsTimelineResponse } from './utils/types'

import {
  ANALYTICS_DELAY_TO_NOTIFICATE,
  ANALYTICS_DATE_STORAGE_KEY,
} from './utils/const'
import { DEBOUNCE_DELAY, FRACTION_DIGITS_MAX, NOTIFICATION_DELAY } from '@/const/common'

import {
  compareRequestWithResponse,
  convertToSeconds,
  prepareDatesRangeSlice,
} from './utils/helpers'
import { handleCatchedError } from '@/helpers/common'
import { generateUniqueRequestId } from '@/services/ws/utils/helpers'
import { prepareResponseError } from '../utils/helpers'
import { numberFormat } from '@/helpers/numbers'

import { useRepositoriesStore } from '../repositories/index'
import { useRepoLocalStorage } from '@/hooks/repoLocalStorage'
import { useSettingsStore } from '../settings'
import { useNotifications } from '@/plugins/notification'
import { useUserStore } from '../user'

import api from '@/store/api'
import faro from '@/services/faro'

import useCommitmentsStore from './commitments'
import useIncomeStore from './income'
import useIrrMoicStore from './irr_moic'
import usePerformanceStore from './performance'
import usePositionStore from './position'
import useTreeStore from './tree'
import useTreePivotStore from './tree_pivot'
import useValueStore from './value'

export default defineStore('analytics', () => {
  // INIT
  const repositoriesStore = useRepositoriesStore()
  const userStore = useUserStore()
  const settingsStore = useSettingsStore()
  const route = useRoute()

  const { error, warn } = useNotifications()

  const ws = inject<WSInstance>('ws')

  const progress = ref(0)

  const active = reactive<{
    [key in string]?: AnalyticsStoreActiveKeys[]
  }>({})

  const module: AnalyticsStore = {
    commitments: useCommitmentsStore(),
    income: useIncomeStore(),
    irr_moic: useIrrMoicStore(),
    performance: usePerformanceStore(),
    position: usePositionStore(),
    tree: useTreeStore(),
    tree_pivot: useTreePivotStore(),
    value: useValueStore(),
  }

  let abortController = new AbortController()

  const analyticsError = ref<{
    status?: AnalyticsStatus
    message?: string
  }>()
  const path = ref<string[]>([])

  const initFlag = ref(false)
  const isWaitingToRefresh = ref(false)
  const trigger = ref(0)

  const requestsMap = ref<AnalyticsRequestsMap>(new Map())
  const timelineDates = ref<string[]>([])

  const repositoryId = computed(() => repositoriesStore.getCurrentId)
  const repositoryTimeline = computed(() => {
    return repositoriesStore.getCurrentTimeline
  })
  const repositoryTimelineEnd = computed(
    () => repositoriesStore.getCurrentTimelineEnd,
  )
  const repositoryTimelinePeriods = computed(
    () => repositoryTimeline.value?.periods,
  )
  const currentDashboardKey = computed(() => route.params.slug as string)
  const lockedPath = computed(() => {
    const settings = repositoriesStore.getCurrentUISettings
    return settings?.dashboards?.[currentDashboardKey.value]?.path
  })

  const storageDate = useRepoLocalStorage<string | undefined>(
    ANALYTICS_DATE_STORAGE_KEY,
    undefined,
  )

  const isLoading = computed(() => {
    return (
      (isActive('income') && module.income.isLoading) ||
      (isActive('irr_moic') && module.irr_moic.isLoading) ||
      (isActive('performance') && module.performance.isLoading) ||
      (isActive('position') && module.position.isLoading) ||
      (isActive('tree') && module.tree.isLoading) ||
      (isActive('tree_pivot') && module.tree_pivot.isLoading) ||
      (isActive('value') && module.value.isLoading)
    )
  })

  // GETTERS

  const getAnalyticsParams = computed(() => ({
    ...repositoryTimeline.value,
    end: repositoryTimelineEnd.value,
    include_end: true,
    currency: repositoriesStore.getCurrentCurrency,
  }))

  const getActiveKeys = computed(() => {
    const keys = reduce(
      Object.values(active),
      (acc: string[], value) => {
        if (!value) return acc
        return [...acc, ...value]
      },
      [],
    )
    return reduce(
      keys,
      (acc, item) => {
        const key = item as keyof AnalyticsStore
        if (acc.includes(key)) return acc
        acc.push(key)
        return acc
      },
      [] as (keyof AnalyticsStore)[],
    )
  })

  const isActive = (key: keyof AnalyticsStore) =>
    getActiveKeys.value.includes(key)

  const getDate = computed(() => {
    if (!storageDate.value) return
    return storageDate.value
  })

  const getPath = computed(() => {
    return path.value?.length ? path.value : [AnalyticsField.REPO_ROOT]
  })

  const getEffectivePath = computed(() => {
    return lockedPath.value ?? getPath.value
  })

  const getDatesRangeWithZeroDate = computed(() => timelineDates.value)

  const getDatesRange = computed(() => {
    return prepareDatesRangeSlice(
      getDatesRangeWithZeroDate.value,
      repositoryTimelinePeriods.value,
    )
  })

  const pathRootId = computed(() => {
    return getEffectivePath.value.at(-1)
  })

  const getError = computed(() => analyticsError.value)

  // SETTERS

  const setDate = (data?: string) => {
    storageDate.value = data
  }

  const setActive = (key: string, data: AnalyticsStoreActiveKeys[]) => {
    active[key] = data
  }

  const setInActive = (key: string) => {
    delete active[key]
  }

  const setPath = (data: string[]) => {
    path.value = data
  }

  const markAsDeprecated = (
    exclude = [] as (keyof AnalyticsStore)[],
    flag = true,
  ) => {
    isWaitingToRefresh.value = false
    analyticsError.value = undefined
    Object.keys(module).forEach(item => {
      const key = item as keyof AnalyticsStore
      if (exclude?.includes(key)) return
      module[key].isDeprecated = flag
    })
    trigger.value++
  }

  const markAsWaitingToRefresh = () => {
    isWaitingToRefresh.value = true
  }

  // ACTIONS

  const valueFormatter = (value: number, fractionDigits?: number, withSign = true) => {
    return numberFormat(value, {
      currency: withSign ? repositoriesStore.getCurrentCurrencySymbol : undefined,
      fractionDigits:
        fractionDigits && fractionDigits < settingsStore.getAnalyticsDecimalPrecisions
          ? fractionDigits
          : settingsStore.getAnalyticsDecimalPrecisions,
    })
  }

  const amountFormatter = (value: number) => {
    return numberFormat(value, {
      fractionDigitsMax: FRACTION_DIGITS_MAX,
    })
  }

  const percentFormatter = (value: number, withSign = true) => {
    return `${numberFormat(value)}${withSign ? '%' : ''}`
  }

  const checkIfViewNeedToUpdate = (
    acc: string[],
    key: keyof AnalyticsStore,
  ) => {
    if (module[key] && (module[key].isDeprecated || !module[key].isInit)) {
      acc.push(key)
    }
    return acc
  }

  const hasPendingUpdates = computed(
    () => reduce(getActiveKeys.value, checkIfViewNeedToUpdate, []).length > 0,
  )

  const changeModulesLoadingFlag = (views: string[], flag: boolean) => {
    views.forEach(view => {
      const key = view as keyof AnalyticsStore
      module[key].isLoading = flag
    })
  }

  const showAnalyticsDuration = async (ms: number) => {
    const seconds = convertToSeconds(ms)
    if (seconds > ANALYTICS_DELAY_TO_NOTIFICATE) {
      await warn(
        {
          message: `Analytics calculated in ${seconds} sec`,
        },
        NOTIFICATION_DELAY,
      )
      faro?.measure(
        'Analytics delay',
        {
          analyticLoadingTime: seconds,
        },
        {
          repositoryId: repositoryId.value,
          ...getAnalyticsParams.value,
        },
      )
    }
  }

  const sendAnalyticsRequest = (
    data: string,
    additionalParams: Record<string, any>,
  ) => {
    const rid = repositoryId.value
    if (!rid) return
    const params = {
      rid,
      params: {
        ...getAnalyticsParams.value,
        ...additionalParams,
        data,
      },
    }
    const uniqueId = generateUniqueRequestId()
    requestsMap.value.set(uniqueId, {
      rid,
      data,
      timeline: getAnalyticsParams.value,
      params: additionalParams,
    })
    progress.value = 0
    ws?.send('analytics', 'get', uniqueId, params)
  }

  const fetch = async (inputParams: Record<string, any>, repoId = repositoryId.value, analyticsParams = getAnalyticsParams.value) => {
    const params = {
      ...analyticsParams,
      ...inputParams,
    }
    try {
      const result = await api.get(
        `repositories/${repoId}/analytics`,
        {
          params,
          signal: abortController.signal,
        },
      )
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    }
  }

  const fetchTimelineDates = useDebounceFn(async () => {
    const { periods, freq, end, end_is_today } = repositoryTimeline.value
    if (!repositoryId.value || !periods || !freq || !end) return
    try {
      const result = await api.get<AnalyticsTimelineResponse>(
        'analytics/timeline',
        {
          params: { periods, freq, end, end_is_today, include_end: true },
          signal: abortController.signal,
        },
      )
      timelineDates.value = result.data.dates
    } catch (e) {
      const { message } = e as Error
      if (message === 'canceled') return
      handleCatchedError(prepareResponseError(e), {
        repositoryId: repositoryId.value,
        ...repositoryTimeline.value,
      })
    }
  }, DEBOUNCE_DELAY)

  const updateModuleData = async (key: keyof AnalyticsStore, data: any) => {
    await module[key].setList(data)
    module[key].isLoading = false
  }

  const refreshAnalytics = useDebounceFn(async () => {
    if (__IS_WS_DISABLED__) cancel()
    analyticsError.value = undefined
    // checking analytics stores for deprecated
    const views = reduce(getActiveKeys.value, checkIfViewNeedToUpdate, [])
    if (!views.length) return
    const startTime = new Date().getTime()
    try {
      changeModulesLoadingFlag(views, true)
      const promises: Promise<any>[] = []
      if (views.includes('position')) {
        const accounts = JSON.stringify([pathRootId.value])
        if (__IS_WS_DISABLED__) {
          promises.push(await fetch({ accounts, data: 'position' }))
        } else {
          sendAnalyticsRequest('position', {
            accounts,
          })
        }
      }
      const remaindedViews = views.filter(view => view !== 'position')
      if (remaindedViews.length) {
        const root = pathRootId.value
        const data = remaindedViews.join(',')
        if (__IS_WS_DISABLED__) {
          promises.push(await fetch({ root, data }))
        } else {
          sendAnalyticsRequest(data, {
            root,
          })
        }
      }
      if (__IS_WS_DISABLED__) {
        const result = await Promise.all(promises)
        const concatResult = Object.assign({}, ...result)
        await Promise.all(views.map(async view => {
          const key = view as keyof AnalyticsStore
          await updateModuleData(key, concatResult)
        }))
        changeModulesLoadingFlag(views, false)
        const waitingTime = new Date().getTime() - startTime
        if (waitingTime) {
          showAnalyticsDuration(waitingTime)
        }
      }
    } catch (e) {
      if (__IS_WS_DISABLED__) {
        const { message } = e as Error
        if (message === 'canceled') return
        handleError(views, message)
      }
    } finally {
      if (__IS_WS_DISABLED__) {
        initFlag.value = true
      }
    }
  }, DEBOUNCE_DELAY)

  const refresh = (exclude?: (keyof AnalyticsStore)[]) => {
    getActiveKeys.value.forEach(key => {
      if (exclude?.includes(key)) return
      module[key].isLoading = true
    })
    nextTick(() => {
      getActiveKeys.value.forEach(key => {
        if (exclude?.includes(key)) return
        module[key].setList({} as any)
      })
    })
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  const cancelWSRequests = () => {
    requestsMap.value = new Map()
  }

  const clearModules = () => {
    Object.values(module).forEach((item: any) => {
      item.clear()
    })
  }

  const clear = (whole = true) => {
    if (whole) {
      analyticsError.value = undefined
    }
    initFlag.value = false
    isWaitingToRefresh.value = false
    setPath([])
    setDate(undefined)
    timelineDates.value = []
    cancelWSRequests()
    clearModules()
  }

  const handleError = async (views: string[], message: string | string[], status?: AnalyticsStatus) => {
    const errorMessage = Array.isArray(message) ? message.join(', ') : message
    analyticsError.value = {
      status,
      message: errorMessage,
    }
    changeModulesLoadingFlag(views, false)
    if (!status || !(Object.values(AnalyticsStatus)).includes(status)) {
      handleCatchedError(errorMessage, {
        repositoryId: repositoryId.value || '',
        ...getAnalyticsParams.value,
      })
      await error({
        message: 'Analytics calculated with an error',
      })
    }
  }

  const checkWSResult = (requestId: string) => {
    const request = requestsMap.value.get(requestId)
    return compareRequestWithResponse(
      request,
      repositoryId.value,
      getAnalyticsParams.value,
      pathRootId.value,
    )
  }

  watch(getEffectivePath, (value, prev) => {
    if (!value || isEqual(value, prev)) return
    markAsDeprecated(['tree', 'irr_moic'])
  })

  watch(
    () => [getActiveKeys.value, repositoryId.value, trigger.value],
    (value, prev) => {
      if (!repositoryId.value || isEqual(value, prev) || getError.value) return
      refreshAnalytics()
    },
    { deep: true, immediate: true },
  )

  watch(
    () => [repositoryTimeline.value, repositoryId.value],
    (value, prev) => {
      if (!repositoryId.value || isEqual(value, prev)) return
      fetchTimelineDates()
    },
    { deep: true, immediate: true },
  )

  watch(getDatesRange, values => {
    if (!values.length || (getDate.value && values.includes(getDate.value)))
      return
    setDate(values.at(-1))
  })

  watch(
    () => userStore.isAuthenticated,
    value => {
      if (!value || __IS_WS_DISABLED__) return
      ws?.subscribe(
        'analytics',
        '_get',
        async (message: WSResponse<any>, requestId: string) => {
          if (!checkWSResult(requestId)) return
          if (message.data.progress) {
            progress.value = message.data.progress
          }
        },
      )
      ws?.subscribe(
        'analytics',
        'get',
        async (message: WSResponse<any>, requestId: string) => {
          initFlag.value = true
          if (!checkWSResult(requestId)) return
          progress.value = 100
          const { data, errors, status } = message
          if (errors) {
            const views = Object.keys(module)
            handleError(views, errors, status as AnalyticsStatus)
            return
          }
          const request = requestsMap.value.get(requestId)
          request?.data.split(',').forEach(view => {
            if (view in module) {
              const key = view as AnalyticsStoreActiveKeys
              updateModuleData(key, data)
            }
          })
          if (data.total_compute_time) {
            showAnalyticsDuration(data.total_compute_time)
          }
          if (import.meta.env.DEV && data.analytics_diagnostics) {
            const diag = data.analytics_diagnostics as {
              total_compute_time_ms: number
              data_time_ms: number
              compute_time_ms: number
              metrics_count: number
              metrics: Array<{ label: string; duration_ms: number; description: string }>
            }
            console.debug(
              '[analytics diagnostics]',
              `total=${diag.total_compute_time_ms}ms`,
              `data=${diag.data_time_ms}ms`,
              `compute=${diag.compute_time_ms}ms`,
              `metrics=${diag.metrics_count}`,
              diag.metrics,
            )
          }
        },
      )
    },
    { immediate: true },
  )

  return {
    initFlag,
    isLoading,
    hasPendingUpdates,

    progress,

    module,
    isActive,
    isWaitingToRefresh,

    fetch,

    getAnalyticsParams,

    getDate,
    getDatesRange,
    getDatesRangeWithZeroDate,
    getPath,
    getEffectivePath,
    getRoot: pathRootId,
    getError,

    setDate,
    setActive,
    setInActive,
    setPath,

    markAsDeprecated,
    markAsWaitingToRefresh,

    valueFormatter,
    amountFormatter,
    percentFormatter,

    refresh,
    cancel,
    clear,
  }
})

type AnalyticsCommitmentsStore = ReturnType<typeof useCommitmentsStore>
type AnalyticsIncomeStore = ReturnType<typeof useIncomeStore>
type AnalyticsIrrMoicStore = ReturnType<typeof useIrrMoicStore>
type AnalyticsPerformanceStore = ReturnType<typeof usePerformanceStore>
type AnalyticsPositionStore = ReturnType<typeof usePositionStore>
type AnalyticsTreeStore = ReturnType<typeof useTreeStore>
type AnalyticsTreePivotStore = ReturnType<typeof useTreePivotStore>
type AnalyticsValueStore = ReturnType<typeof useValueStore>

type AnalyticsStore = {
  commitments: AnalyticsCommitmentsStore
  income: AnalyticsIncomeStore
  irr_moic: AnalyticsIrrMoicStore
  performance: AnalyticsPerformanceStore
  position: AnalyticsPositionStore
  tree: AnalyticsTreeStore
  tree_pivot: AnalyticsTreePivotStore
  value: AnalyticsValueStore
}

type AnalyticsStoreActiveKeys = keyof AnalyticsStore
